import flatten from 'lodash/flatten';
import { assert } from 'ts-essentials';
import { MutationSource } from '../constants/mutation-constants';
import { OperationType } from '../constants/operation-constants';
import { record } from '../utils/recorder';
import { JSF32RandomIDGenerator } from '../utils/id-generator';
import { CommandData } from '../app/command-data';
import { CommandEventTypes, PresentationEventTypes } from './event-manager/event-types';
import type { IOTTransform } from '@tencent/slide-sdk-ot-transform';
import type { IEventManager } from './event-manager';
import type { Mutation } from '../mutation/base/base-mutation';

export enum CommandSource {
    None = 'none',
    Undo = 'undo',
    Redo = 'redo',
    Editor = 'editor',
    Collaborator = 'collaborator',
    Offline = 'offline',
}

export enum TransformSource {
    UndoRedo = 'undo-redo',
    Offline = 'offline',
    Collaboration = 'collaboration',
}

export interface ICommandManager {
    /**
     * 是否可以执行撤销操作
     */
    canUndo: boolean;
    /**
     * 是否可以执行重做操作
     */
    canRedo: boolean;
    /**
     * 撤销栈大小
     */
    undoCommandSize: number;
    /**
     * 重做栈大小
     */
    redoCommandSize: number;
    /**
     * 执行撤销操作
     */
    undo(): void;
    /**
     * 执行重做操作
     */
    redo(): void;
    /**
     * 设置最大的 command 数量
     */
    setMaxCommand(maxCommand: number): void;
    /**
     * 清空堆栈信息
     */
    clear(): void;
    /**
     * 获取 undo 堆栈中的数据
     */
    getUndoCommandListData(): Readonly<ICommandData>[];
}

const MODULE_NAME = '[SDK][UR]';

export class CommandManager implements ICommandManager {
    private undoCommandList: Command[] = [];
    private redoCommandList: Command[] = [];
    private maxCommand = 100;
    private idGenerator: JSF32RandomIDGenerator;

    constructor(
        private events: IEventManager,
        private presentationProvider: {
            applyMutations(mutations: Mutation[], mutationSource: MutationSource): Mutation[];
        },
        private logger?: Pick<typeof console, 'debug' | 'error' | 'info' | 'log' | 'warn'>,
        private getTransformManager?: () => IOTTransform | undefined,
    ) {
        this.idGenerator = new JSF32RandomIDGenerator();
        this.init();
    }

    public setMaxCommand(maxCommand: number) {
        if (maxCommand < 0) {
            throw new Error('maxCommand must be greater than 0');
        }
        this.maxCommand = maxCommand;
    }

    public get canUndo() {
        return this.getCanRedoUndoCommand(this.undoCommandList).index > -1;
    }

    public get canRedo() {
        return this.getCanRedoUndoCommand(this.redoCommandList).index > -1;
    }

    public get undoCommandSize() {
        return this.undoCommandList.length;
    }

    public get redoCommandSize() {
        return this.redoCommandList.length;
    }

    private get status() {
        return {
            canUndo: this.canUndo,
            canRedo: this.canRedo,
        };
    }

    public getUndoCommandListData() {
        return this.undoCommandList.map((command) => command.getData());
    }

    @record(OperationType.Undo)
    public undo() {
        const { index, command } = this.getCanRedoUndoCommand(this.undoCommandList);
        if (!command) return;

        let hasConflict = false;
        // 如果是非栈顶的 command 需要进行转换
        if (index !== this.undoCommandList.length - 1) {
            hasConflict = true;
            this.transformCommand({ index, command }, this.undoCommandList);
        }
        this.undoCommandList.splice(index, 1);
        this.logger?.info(MODULE_NAME, 'execute undo command:', command.getData());
        command.execute(hasConflict);
        if (!command.canDeprecate) {
            this.redoCommandList.push(command.reverse());
        }
        this.events.emit(CommandEventTypes.UndoRedoChange, {
            ...this.status,
            commandId: command.getData().commandId,
            source: CommandSource.Undo,
            mutationsType: command.getData().undoCommand.mutations.map((mut) => mut.type),
        });
    }

    @record(OperationType.Redo)
    public redo() {
        const { index, command } = this.getCanRedoUndoCommand(this.redoCommandList);
        if (!command) return;

        let hasConflict = false;
        // 如果是非栈顶的 command 需要进行转换
        if (index !== this.redoCommandList.length - 1) {
            hasConflict = true;
            this.transformCommand({ index, command }, this.redoCommandList);
        }
        this.redoCommandList.splice(index, 1);
        this.logger?.info(MODULE_NAME, 'execute redo command:', command.getData());
        command.execute(hasConflict);

        if (!command.canDeprecate) {
            this.undoCommandList.push(command.reverse());
        }
        this.events.emit(CommandEventTypes.UndoRedoChange, {
            ...this.status,
            commandId: command.getData().commandId,
            source: CommandSource.Redo,
            mutationsType: command.getData().undoCommand.mutations.map((mut) => mut.type),
        });
    }

    public clear() {
        const redoCommandList = this.redoCommandList.splice(0, this.redoCommandList.length);
        const undoCommandList = this.undoCommandList.splice(0, this.undoCommandList.length);
        this.events.emit(CommandEventTypes.ClearCommand, [
            ...redoCommandList.map((command) => command.getData().commandId),
            ...undoCommandList.map((command) => command.getData().commandId),
        ]);
    }

    private init() {
        this.events.on(PresentationEventTypes.CommitCommand, this.handleCommitMutations);
        return this;
    }

    private addCommand(command: Command) {
        if (command.canExecute) {
            this.clearRedoCommandList();
            this.undoCommandList.push(command);
            if (this.undoCommandList.length > this.maxCommand) {
                this.undoCommandList.shift();
            }
            return;
        }
        this.undoCommandList.push(command);
        this.redoCommandList.push(command);
        this.removeUnusedCollaborationCommand();
    }

    private createCommand(redoCommand: IRedoUndoCommand, undoCommand: IRedoUndoCommand, type: MutationSource) {
        return new Command(
            redoCommand,
            undoCommand,
            type,
            this.idGenerator.next(),
            (mutations) => {
                try {
                    const invertedMutations = this.presentationProvider?.applyMutations(
                        mutations,
                        MutationSource.UndoRedo,
                    );
                    return invertedMutations;
                } catch (err: any) {
                    this.events.emit(CommandEventTypes.Error, err);
                }
                return [];
            },
            this.logger,
        );
    }

    private clearRedoCommandList() {
        const redoCommandList = this.redoCommandList.splice(0, this.redoCommandList.length);
        this.events.emit(
            CommandEventTypes.ClearCommand,
            redoCommandList.map((command) => command.getData().commandId),
        );
    }

    private handleCommitMutations = (
        mutations: Mutation[],
        invertedMutations: Mutation[][],
        source: MutationSource,
    ) => {
        const undoCommandMuts = flatten(invertedMutations.reverse());
        if (undoCommandMuts.length === 0) {
            return;
        }
        const command = this.createCommand(
            {
                mutations,
            },
            {
                mutations: undoCommandMuts,
            },
            source,
        );
        this.addCommand(command);
        this.events.emit(CommandEventTypes.UndoRedoChange, {
            ...this.status,
            commandId: command.getData().commandId,
            source: this.getCommandSource(source),
            mutationsType: command.getData().redoCommand.mutations.map((mut) => mut.type),
        });
    };

    private removeUnusedCollaborationCommand() {
        const undoCommandItemIndex = this.undoCommandList.findIndex((command) => command.canExecute);
        const redoCommandItemIndex = this.redoCommandList.findIndex((command) => command.canExecute);
        const removedUndoCommandList = (() => {
            if (undoCommandItemIndex > 0) return this.undoCommandList.splice(0, undoCommandItemIndex);
            if (undoCommandItemIndex < 0) return this.undoCommandList.splice(0, this.undoCommandList.length);
        })();
        const removedRedoCommandList = (() => {
            if (redoCommandItemIndex > 0) return this.redoCommandList.splice(0, redoCommandItemIndex);
            if (redoCommandItemIndex < 0) return this.redoCommandList.splice(0, this.redoCommandList.length);
        })();
        if (removedUndoCommandList || removedRedoCommandList) {
            this.events.emit(
                CommandEventTypes.ClearCommand,
                [...(removedUndoCommandList || []), ...(removedRedoCommandList || [])].map(
                    (command) => command.getData().commandId,
                ),
            );
        }
    }

    private getCanRedoUndoCommand(commandList: Command[]) {
        for (let i = commandList.length - 1; i >= 0; i--) {
            if (commandList[i].canExecute) {
                return {
                    index: i,
                    command: commandList[i],
                };
            }
        }
        return {
            index: -1,
            command: null,
        };
    }

    private getCommandSource(source: MutationSource) {
        switch (source) {
            case MutationSource.Editor:
                return CommandSource.Editor;
            case MutationSource.Offline:
                return CommandSource.Offline;
            case MutationSource.Collaborator:
            case MutationSource.CollaboratorConflict:
                return CommandSource.Collaborator;
        }
        return CommandSource.None;
    }

    private transformCommand({ command, index }: { command: Command; index: number }, basicCommandList: Command[]) {
        for (let i = index + 1; i < basicCommandList.length; i++) {
            const collaborationCommand = basicCommandList[i];
            const localUndoCommand = command.getData().undoCommand;
            const collaborationRedoCommand = collaborationCommand.getData().redoCommand;
            this.logger?.info(
                MODULE_NAME,
                'followCommand start transform, localUndoCommand:',
                localUndoCommand,
                'collaborationRedoCommand:',
                collaborationRedoCommand,
            );
            const [followLocalUndoCommand, followCollaborationRedoCommand] = this.followCommand(
                localUndoCommand,
                collaborationRedoCommand,
                true,
            );
            this.logger?.info(
                MODULE_NAME,
                'followCommand end transform, followLocalUndoCommand:',
                followLocalUndoCommand,
                'followCollaborationRedoCommand:',
                followCollaborationRedoCommand,
            );
            command.setUndoCommand(followLocalUndoCommand);
            collaborationCommand.setRedoCommand(followCollaborationRedoCommand);
        }
    }

    private followCommand(
        undoRedoCommandA: IRedoUndoCommand,
        undoRedoCommandB: IRedoUndoCommand,
        isAAfterB: boolean,
    ): [IRedoUndoCommand, IRedoUndoCommand] {
        const transformManager = this.getTransformManager?.();
        assert(transformManager, 'transformManager is undefined');
        const { transformedA: followCommandsA, transformedB: followCommandsB } = transformManager.syncTransform(
            [CommandData.fromMutations(undoRedoCommandA.mutations).getCommandData()],
            [CommandData.fromMutations(undoRedoCommandB.mutations).getCommandData()],
            isAAfterB,
            TransformSource.UndoRedo,
        );
        return [
            {
                ...undoRedoCommandA,
                mutations: CommandData.fromMutationData(followCommandsA[0]?.mutations ?? []).getMutations(),
            },
            {
                ...undoRedoCommandB,
                mutations: CommandData.fromMutationData(followCommandsB[0]?.mutations ?? []).getMutations(),
            },
        ];
    }
}

export interface ICommand {
    canExecute: boolean;
    canSubmit: boolean;
    canDeprecate: boolean;
    execute(hasConflict: boolean): void;
    reverse(): ICommand;
    getData(): ICommandData;
    setRedoCommand(redoCommand: IRedoUndoCommand): void;
    setUndoCommand(undoCommand: IRedoUndoCommand): void;
}

export interface IRedoUndoCommand {
    mutations: Mutation[];
}

interface ICommandData {
    type: MutationSource;
    revision: number;
    commandId: string;
    redoCommand: IRedoUndoCommand;
    undoCommand: IRedoUndoCommand;
}

export class Command implements ICommand {
    private revision: number;

    constructor(
        private redoCommand: IRedoUndoCommand,
        private undoCommand: IRedoUndoCommand,
        private type: MutationSource,
        private commandId: string,
        private apply: (mutations: Mutation[]) => Mutation[],
        private logger?: Pick<typeof console, 'debug' | 'error' | 'info' | 'log' | 'warn'>,
    ) {}

    public get canExecute() {
        return [MutationSource.None, MutationSource.Editor].includes(this.type);
    }

    public get canSubmit() {
        return [MutationSource.Editor].includes(this.type);
    }

    public get canDeprecate() {
        return this.redoCommand.mutations.length === 0 && this.undoCommand.mutations.length === 0;
    }

    public execute(hasConflict: boolean) {
        const invertedMutations = this.apply(this.undoCommand.mutations);
        if (hasConflict) {
            this.redoCommand.mutations = invertedMutations;
            this.logger?.info(MODULE_NAME, 'command updated:', this.getData());
        }
    }

    /**
     * 会提供一个新的 command，反转 redoCommand 和 undoCommand
     * @returns
     */
    public reverse() {
        return new Command(this.undoCommand, this.redoCommand, this.type, this.commandId, this.apply, this.logger);
    }

    public getData(): Readonly<ICommandData> {
        return {
            type: this.type,
            revision: this.revision,
            commandId: this.commandId,
            redoCommand: this.redoCommand,
            undoCommand: this.undoCommand,
        };
    }

    public setRedoCommand(redoCommand: IRedoUndoCommand) {
        this.redoCommand = redoCommand;
    }

    public setUndoCommand(undoCommand: IRedoUndoCommand) {
        this.undoCommand = undoCommand;
    }
}
